import { useId, useState } from 'react';
import { X } from 'lucide-react';
import { AdminLoadingState } from '../../components/primitives/admin-loading-state.js';
import { AdminSearchInput } from '../../components/primitives/admin-search-input.js';
import { useDebouncedValue } from '../../lib/use-debounced-value.js';
import { useAdminMediaAsset, useAdminMediaLibrary } from '../../media/hooks/use-media.js';
import type { ProductFormState } from '../pages/product-editor-page.js';

export interface ProductSeoTabProps {
  form: ProductFormState;
  onChange: (patch: Partial<ProductFormState>) => void;
}

export function ProductSeoTab({ form, onChange }: ProductSeoTabProps) {
  const titleId = useId();
  const descriptionId = useId();
  const canonicalId = useId();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const { data: library, isLoading: libraryLoading } = useAdminMediaLibrary({
    search: debouncedSearch || undefined,
    kind: 'image',
    limit: 20,
  });
  const { data: selectedOgImage } = useAdminMediaAsset(form.ogImageMediaAssetId || null);

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={titleId} className="mb-1 block text-sm font-medium text-gray-700">
          SEO title
        </label>
        <input
          id={titleId}
          value={form.metaTitle}
          onChange={(event) => onChange({ metaTitle: event.target.value })}
          maxLength={300}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>
      <div>
        <label htmlFor={descriptionId} className="mb-1 block text-sm font-medium text-gray-700">
          SEO description
        </label>
        <textarea
          id={descriptionId}
          value={form.metaDescription}
          onChange={(event) => onChange({ metaDescription: event.target.value })}
          maxLength={300}
          rows={3}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
      </div>
      <div>
        <label htmlFor={canonicalId} className="mb-1 block text-sm font-medium text-gray-700">
          Canonical URL <span className="font-normal text-gray-400">(optional)</span>
        </label>
        <input
          id={canonicalId}
          type="url"
          placeholder="https://tsgecart.com/product/..."
          value={form.canonicalUrl}
          onChange={(event) => onChange({ canonicalUrl: event.target.value })}
          maxLength={220}
          className="focus-visible:ring-brand w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
        />
        <p className="mt-1 text-xs text-gray-400">
          Overrides the default canonical URL derived from the product slug (General tab) -- leave blank
          unless this product&apos;s content is duplicated at another URL.
        </p>
      </div>

      <div>
        <p className="mb-1 block text-sm font-medium text-gray-700">Open Graph image</p>
        {form.ogImageMediaAssetId ? (
          <div className="flex items-center gap-3 rounded-xl border border-gray-100 p-2">
            {selectedOgImage?.url ? (
              <img
                src={selectedOgImage.url}
                alt="OG image preview"
                className="h-16 w-16 rounded-lg object-cover"
              />
            ) : (
              <div className="h-16 w-16 rounded-lg bg-gray-100" aria-hidden="true" />
            )}
            <button
              type="button"
              onClick={() => onChange({ ogImageMediaAssetId: '' })}
              className="flex items-center gap-1 rounded-full border border-gray-200 px-2.5 py-1 text-xs text-red-600 hover:bg-red-50"
            >
              <X className="h-3.5 w-3.5" aria-hidden="true" />
              Remove
            </button>
          </div>
        ) : pickerOpen ? (
          <div className="rounded-xl border border-gray-100 p-3">
            <div className="mb-2 flex items-center justify-between">
              <AdminSearchInput value={search} onChange={setSearch} placeholder="Search media library…" />
              <button
                type="button"
                onClick={() => setPickerOpen(false)}
                className="rounded-full px-3 py-1 text-sm text-gray-500 hover:bg-gray-100"
              >
                Close
              </button>
            </div>
            {libraryLoading ? (
              <AdminLoadingState label="Loading media library" />
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
                {(library ?? []).map((asset) => (
                  <button
                    key={asset.id}
                    type="button"
                    onClick={() => {
                      onChange({ ogImageMediaAssetId: asset.id });
                      setPickerOpen(false);
                    }}
                    className="overflow-hidden rounded-lg border border-gray-100"
                    title="Use this image as the OG image"
                  >
                    {asset.url ? (
                      <img src={asset.url} alt="" className="h-16 w-full object-cover" />
                    ) : (
                      <div className="h-16 w-full bg-gray-100" aria-hidden="true" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="focus-visible:ring-brand rounded-full border border-dashed border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2"
          >
            Pick an image from the media library
          </button>
        )}
        <p className="mt-1 text-xs text-gray-400">
          Shown when this product is shared on social platforms. Falls back to the primary product image
          (Media tab) when not set.
        </p>
      </div>

      <p className="text-xs text-gray-400">
        OG/Twitter title and description beyond the image are not editable yet in this milestone.
      </p>
    </div>
  );
}
